import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import BlockRenderer from "../components/BlockRenderer";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function PublicPage() {
  const { slug } = useParams();
  const effectiveSlug = slug || "home";
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    setPage(null); setError(null);
    api.get(`/pages/${effectiveSlug}`).then((r) => setPage(r.data)).catch(() => setError("not_found"));
  }, [effectiveSlug]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (page?.system_kind === "blog") {
      api.get("/blog").then((r) => setPosts(r.data || [])).catch(() => {});
    }
  }, [page]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <SEO title="Page not found" path={`/${effectiveSlug}`} noIndex />
        <h1 className="font-serif-display text-5xl mb-4">Page not found</h1>
        <p className="text-neutral-600 mb-8">The page you're looking for doesn't exist.</p>
        <Link to="/" className="btn-primary-burgundy">Return home</Link>
      </div>
    );
  }
  if (!page) return <div className="py-32 text-center text-neutral-500" data-testid="page-loading">Loading…</div>;

  const seoPath = effectiveSlug === "home" ? "/" : `/${page.slug}`;
  const seoTitle = effectiveSlug === "home" ? null : page.title;
  const seoDesc = page.seo_description || settings?.site_description;
  const seoKeywords = page.seo_keywords || settings?.seo_keywords;
  const seoFullTitleOverride = page.seo_title || null;

  // Per-page JSON-LD structured data (Schema.org). Helps Google understand the service.
  const siteUrlBase = (settings?.site_url || "https://spiritandassociates.org").replace(/\/$/, "");
  let jsonLd = null;
  if (page.slug === "5329-evaluations") {
    jsonLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "5329 Threat Risk of Harm Evaluation",
      "serviceType": "5329 Threat Risk of Harm Evaluation",
      "description": "Court-recognized Pennsylvania 5329 threat risk of harm evaluations for firearms-related cases. Same-week appointments serving York, Lancaster, Dauphin, Adams, Lebanon, Berks, Perry, and Cumberland counties.",
      "provider": {
        "@type": "ProfessionalService",
        "name": "Spirit & Associates",
        "url": siteUrlBase,
        "telephone": "+1-717-616-2212",
        "email": "sa.evaluations@gmail.com",
        "areaServed": [
          {"@type": "AdministrativeArea", "name": "York County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Lancaster County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Dauphin County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Adams County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Lebanon County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Berks County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Perry County, Pennsylvania"},
          {"@type": "AdministrativeArea", "name": "Cumberland County, Pennsylvania"}
        ]
      },
      "areaServed": {"@type": "State", "name": "Pennsylvania"},
      "url": `${siteUrlBase}/5329-evaluations`,
      "availableChannel": [
        {"@type": "ServiceChannel", "name": "Phone", "servicePhone": "+1-717-616-2212"},
        {"@type": "ServiceChannel", "name": "Text", "servicePhone": "+1-717-616-2212"},
        {"@type": "ServiceChannel", "name": "Email", "serviceUrl": "mailto:sa.evaluations@gmail.com"}
      ]
    };
  }

  return (
    <div className="fade-in" data-testid={`public-page-${page.slug}`}>
      <SEO
        title={seoTitle}
        fullTitleOverride={seoFullTitleOverride}
        description={seoDesc}
        path={seoPath}
        keywords={seoKeywords}
        siteUrl={settings?.site_url}
        siteName={settings?.site_name}
        jsonLd={jsonLd}
      />
      <BlockRenderer blocks={page.blocks || []} />

      {page.system_kind === "blog" && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
          {posts.length === 0 ? (
            <p className="text-neutral-500 italic" data-testid="blog-empty">No posts yet. Check back soon.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="blog-list">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
                  {p.cover_image && (
                    <div className="aspect-[4/3] overflow-hidden mb-5 bg-neutral-100">
                      <img src={fileUrl(p.cover_image)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <p className="label-eyebrow mb-2">{new Date(p.created_at).toLocaleDateString()}</p>
                  <h3 className="font-serif-display text-2xl text-[#1a1a1a] group-hover:text-[#6B1F2A] transition-colors leading-tight mb-2">{p.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {page.system_kind === "contact" && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pb-24">
          <ContactForm />
        </section>
      )}
    </div>
  );
}
